/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { ControlStateMixin } from '../../utils/control-state-mixin.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';
import { KEYCODE } from '../../utils/keycode.js';

/**
 * @prop {String} label - Defines the label of the Menu Item.
 * @prop {String} value - Defines the value of the Menu Item.
 * @prop {String} icon - Defines the icon shown before the label. For available icon list, refer Icon page.
 * @prop {String} href - Defines the destination link to be be navigated to when the item is clicked.
 * @prop {Boolean} disabled - If set to `true`, the item will be shown as disabled and won't allow clicks.
 * @prop {Boolean} active - If set to `true`, the style will adapt to represent that the item is currently active.
 * @prop {Boolean} expanded - If chapter is set to true, this will determine whether the chapter is expanded or not. This state switches upon clicking on the arrow.
 * @prop {Boolean} checkbox - If set to `true`, checkbox will be added to menu item.
 * @prop {Boolean} toggle - Default true. Toggles the active property on click event.
 * @prop {String} iconColor - Sets the color of icon added by setting icon property.
 * @attr {String} icon-color - Sets the color of icon added by setting icon property.
 * @prop {Boolean} verticalAlignment - When vertical-alignment property is set, all items inside the menu item are vertical aligned.
 * @attr {String} vertical-alignment -  When vertical-alignment property is set, all items inside the menu item are vertical aligned.
 * @prop {String} iconSize -  Sets the icon size of icon component added through `icon` property. Possible values are `xs, s, m, l`.
 * @attr {String} icon-size -  Sets the icon size of icon component added through `icon` property. Possible values are `xs, s, m, l`..
 * @prop {String} labelRows -  Label can have multiple lines when set. Possible values are `1,2 and auto`.
 * @attr {String} label-rows -  Label can have multiple lines when set. Possible values are `1,2 and auto`.
 * @fires expand - Fired when clicking on the arrow icon (if expanded is false).
 * @fires collapse - Fired when clicking on the arrow icon (if expanded is true).
 */

export class SHMenuItem extends ControlStateMixin(FocusBlurMixin(LitElement)) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      href: { type: String, reflect: true },
      active: { type: Boolean, reflect: true },
      /** @ignore */
      role: { type: String, reflect: true },
      /** @ignore */
      chapter: { type: Boolean, reflect: true },
      expanded: { type: Boolean, reflect: true },
      value: { type: String, reflect: true },
      checkbox: { type: Boolean, reflect: true },
      toggle: { type: Boolean, reflect: true },
      iconColor: { type: String, reflect: true, attribute: 'icon-color' },
      iconSize: { type: String, reflect: true, attribute: 'icon-size' },
      verticalAlignment: { type: Boolean, reflect: true, attribute: 'vertical-alignment' },
      labelRows: { type: String, reflect: true, attribute: 'label-rows' },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
          outline: 0;
          display: block;
          height: fit-content;
        }
        :host([icon]:not([checkbox]):not([vertical-alignment])) .menu-item-wrapper {
          padding: 0.25rem 0;
          min-height: 2.5rem;
        }
        :host([icon]:not([checkbox]):not([vertical-alignment])) .menu-item-wrapper[active] {
          padding: 0.25rem 0.5rem;
        }
        .vertical-item-wrapper {
          display: flex;
          align-items: center;
          width: 100%;
        }
        :host([vertical-alignment]) .vertical-item-wrapper {
          display: block;
          margin: auto;
        }
        :host([vertical-alignment]) #checkbox,
        :host([vertical-alignment]) #menu-item-icon {
          margin: 0rem auto 0.5rem;
        }
        :host([vertical-alignment]) .menu-item-label {
          text-align: center;
          line-height: 1.5rem;
        }
        :host(:not(.touch-device):not([active])[vertical-alignment]) .menu-item-wrapper:hover,
        :host([vertical-alignment]) .menu-item-wrapper[active],
        :host([vertical-alignment]) .menu-item-wrapper {
          width: 100%;
          margin: 0rem;
          padding: 0.5rem;
        }
        :host(:not([label-rows='1'])) #checkbox,
        :host(:not([label-rows='1'])) #menu-item-icon,
        :host(:not([label-rows='1'])) ::slotted(*[slot='functions']),
        :host([vertical-alignment]) ::slotted(*[slot='functions']) {
          align-self: flex-start;
        }
        .menu-item-wrapper {
          height: fit-content;
          height: -ms-fit-content;
          height: -moz-fit-content;
          min-height: 2.5rem;
          width: 100%;
          box-sizing: border-box;
          padding: 0.25rem 0;
          transition: var(--transition-time) all ease-in-out, 0.1s background ease;
          border-radius: 0.125rem;
          display: flex;
          flex-direction: row;
          cursor: pointer;
          outline: none;
          align-items: center;
        }
        :host([chapter]) .item-wrapper > .menu-item-wrapper {
          width: calc(100% - 2rem);
        }
        :host(.chapter-sibling) .menu-item-wrapper {
          overflow: hidden;
        }
        /* active */
        .menu-item-wrapper[active] {
          padding: 0.25rem 0.5rem;
          margin: 0 -0.5rem;
          width: calc(100% + 1rem);
          background: rgba(var(--ui-1), var(--opacity-6));
        }
        .menu-item-wrapper.item-focused {
          outline: var(--focus-outline);
          outline-offset: -0.125rem;
          padding: 0.25rem 0.5rem;
          margin: 0 -0.5rem;
          width: calc(100% + 1rem);
        }
        :host(:not([chapter]).chapter-sibling) .item-wrapper > .menu-item-wrapper.item-focused {
          outline: var(--focus-outline);
          outline-offset: -0.125rem;
          padding: 0.25rem 0.5rem;
          margin: 0 -0.5rem 0 1.5rem;
          width: calc(100% + 1rem);
        }
        :host([icon]:not([checkbox]):not([vertical-alignment]))
          .item-wrapper
          > .menu-item-wrapper.item-focused,
        :host([icon]:not([checkbox]):not([vertical-alignment]))
          .item-wrapper
          > .menu-item-wrapper.item-focused:hover,
        :host([icon]:not([checkbox]):not(.touch-device):not([vertical-alignment]))
          .item-wrapper
          > .menu-item-wrapper.hover {
          padding: 0.25rem 0.5rem !important;
        }
        :host([chapter]) .item-wrapper > .menu-item-wrapper[active],
        :host([chapter]:not(.touch-device):not([active])) .item-wrapper > .menu-item-wrapper:hover,
        :host([chapter]:not(.touch-device):not([active])) .item-wrapper > .menu-item-wrapper.hover {
          width: calc(100% - 1rem);
        }
        /* disabled */
        :host([disabled]) {
          pointer-events: none;
        }
        .menu-item-label {
          font: var(--body-1);
          line-height: 1.5rem;
          font-weight: normal;
          color: var(--text-primary);
          flex: 1;
          transition: var(--transition-time) all ease-in-out;
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
          -moz-osx-font-smoothing: grayscale;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          overflow: hidden;
          -moz-user-select: none;
          -webkit-user-select: none;
          user-select: none;
          position: relative;
          word-break: break-all;
        }
        sh-icon#menu-item-icon {
          position: relative;
          color: var(--text-primary);
          margin-right: 0.5rem;
        }
        :host([vertical-alignment]) ::slotted(*[slot='functions']) {
          margin: 0.25rem 0rem 0rem -1.75rem;
        }
        :host(:not([vertical-alignment])) ::slotted(*[slot='functions']) {
          margin-left: 0.5rem;
        }
        :host(:not([vertical-alignment])[label-rows='1']) ::slotted(*[slot='functions']) {
          position: relative;
          align-self: center;
        }
        .item-wrapper {
          display: flex;
          flex-direction: row;
          text-decoration: none;
          align-items: center;
        }
        /* chapter menu */
        .chapter-wrapper {
          transition: 0.1s all ease-in-out;
        }
        .chapter-wrapper > ::slotted([chapter]) {
          margin-left: -2rem;
          width: calc(100% + 2rem);
        }
        .chapter-wrapper > ::slotted(*) {
          visibility: hidden;
        }
        :host([expanded]) .chapter-wrapper > ::slotted(*) {
          visibility: visible;
        }
        :host(:not([expanded])) > .chapter-wrapper {
          max-height: 0rem;
          overflow: hidden;
          pointer-events: none;
          transform: translateY(-0.5rem);
          opacity: 0;
        }
        :host([chapter]) > .chapter-wrapper {
          display: flex;
          flex-direction: column;
          margin-left: 3.5rem;
        }
        .arrow-icon {
          display: block;
          margin-right: 0.5rem;
        }
        :host([chapter][expanded]) > .item-wrapper > .arrow-icon {
          transform: rotate(90deg);
        }
        :host(:not([chapter]).chapter-sibling) > .item-wrapper > .menu-item-wrapper {
          margin-left: 2rem;
        }
        /* hover */
        /* CSS rules with .hover class are needed for use in dropdown component */
        :host(:not(.touch-device):not([active])) .menu-item-wrapper:hover,
        :host(:not(.touch-device):not([active])) .menu-item-wrapper.hover,
        :host(.hover:not(.touch-device):not([active])) .menu-item-wrapper {
          background: rgba(var(--ui-1), var(--opacity-7));
          padding: 0.25rem 0.5rem;
          margin: 0 -0.5rem;
          width: calc(100% + 1rem);
        }
        :host(.focus:not(.touch-device)) .menu-item-wrapper {
          outline: var(--focus-outline);
          outline-offset: -0.125rem;
        }
        :host([icon]:not([checkbox]):not(.touch-device):not([active]):not([vertical-alignment]))
          .menu-item-wrapper:hover,
        :host([icon]:not([checkbox]):not(.touch-device):not([active])) .menu-item-wrapper.hover,
        :host(.hover[icon]:not([checkbox]):not(.touch-device):not([active])) .menu-item-wrapper {
          background: rgba(var(--ui-1), var(--opacity-7));
          padding: 0.25rem 0.5rem;
        }
        :host(.chapter-sibling:not([chapter]):not(.touch-device)) .menu-item-wrapper:hover,
        :host(.chapter-sibling:not([chapter]):not(.touch-device)) .menu-item-wrapper.hover,
        :host(.hover.chapter-sibling:not([chapter]):not(.touch-device)) .menu-item-wrapper,
        :host(.chapter-sibling:not([chapter])[active]) .menu-item-wrapper {
          margin-left: 1.5rem;
        }
        :host(:not(.touch-device):not([active])) .menu-item-wrapper[disabled] {
          background: transparent;
        }
        :host([disabled]) .menu-item-label {
          color: var(--text-disabled);
        }
        :host([disabled]) #menu-item-icon {
          opacity: 0.2;
        }
        #checkbox {
          width: fit-content;
          width: -moz-fit-content;
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="item-wrapper">
        ${this.chapter
          ? html` <sh-icon
              id="menuExpandIcon"
              ?disabled="${this.disabled}"
              button
              icon="arrow-right-s"
              size="s"
              class="arrow-icon"
              @click="${this._handleExpandIconClick}"
              @focus="${this._handleExpandIconFocus}"
            >
            </sh-icon>`
          : ''}
        <div
          id="menuExpandWrapper"
          class="menu-item-wrapper${!this.chapter ? ' focus-item' : ''}"
          role="button"
          ?active="${this.active}"
          ?disabled="${this.disabled}"
          @click="${this._handleClicked}"
        >
          <div class="vertical-item-wrapper">
            ${this.icon && !this.checkbox
              ? html` <sh-icon
                  .icon="${this.icon}"
                  size="${this.iconSize ? this.iconSize : 'm'}"
                  id="menu-item-icon"
                  .color="${this.iconColor}"
                >
                </sh-icon>`
              : ''}
            ${this.checkbox
              ? html` <sh-checkbox
                  neutral
                  tabindex="-1"
                  label=""
                  ?active="${this.active}"
                  ?disabled="${this.disabled}"
                  id="checkbox"
                ></sh-checkbox>`
              : ''}
            <div
              class="menu-item-label"
              style="-webkit-line-clamp: ${this.labelRows !== '1' &&
              this.labelRows !== '2' &&
              this.labelRows !== 'auto'
                ? '2'
                : this.labelRows}"
            >
              ${this.label}
            </div>
          </div>
          ${!this.verticalAlignment ? html`<slot name="functions" id="functions"></slot>` : ''}
        </div>
        ${this.verticalAlignment ? html`<slot name="functions" id="functions"></slot>` : ''}
      </div>
      <div class="chapter-wrapper">
        <slot id="chapterSlot" @slotchange="${this._chapterSlotObserver}"></slot>
      </div>
    `;
  }

  constructor() {
    super();
    this.label = 'label';
    this.value = '';
    this.toggle = true;
    this.verticalAlignment = false;
    this.iconSize = 'm';
    this.labelRows = '1';
  }

  connectedCallback() {
    super.connectedCallback();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
    if (!this.hasAttribute('role')) {
      this.role = 'listitem';
    }

    this._handleFocus = this._handleFocus.bind(this);
    this.addEventListener('focus', this._handleFocus);
    this._addOrRemoveClass();
  }

  firstUpdated() {
    super.firstUpdated();
    this.shadowRoot.querySelector('.menu-item-label').addEventListener('mouseover', function () {
      if (this.offsetHeight < this.scrollHeight) {
        this.setAttribute('title', this.textContent);
      } else {
        this.removeAttribute('title');
      }
    });
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('disabled')) {
      this._handleDisabledChange();
    }
    if (changedProperties.has('active')) {
      this._handleActiveChange();
    }
    //active not added to list since it is dispatched in handleActiveChange method
    const listOfProperties = ['icon', 'href', 'expanded', 'value'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  _handleFocus() {
    this.onkeyup = (e) => {
      if (
        (e.keyCode === KEYCODE.ENTER || e.keyCode === KEYCODE.SPACE) &&
        e.composedPath()[0] === this
      ) {
        this._handleClicked();
      }
    };
  }

  _chapterSlotObserver() {
    const functionsSlot = this.shadowRoot.querySelector('#functions');
    const hasChildMenuItems =
      this.childElementCount > functionsSlot.assignedNodes({ flatten: true }).length;
    this.chapter = hasChildMenuItems;
    this._addOrRemoveClass();
  }

  _addOrRemoveClass() {
    if (this.parentNode.tagName === 'SH-MENU-ITEM') {
      return;
    }
    const siblingItems = this.parentNode.children;

    if (this.chapter) {
      for (const sibling of siblingItems) {
        sibling.classList.add('chapter-sibling');
      }
    } else if (!this.classList.contains('chapter-sibling')) {
      for (const sibling of siblingItems) {
        if (sibling.chapter) {
          this.classList.add('chapter-sibling');
          break;
        }
      }
    }
  }

  _handleDisabledChange() {
    for (const child of this.children) {
      child.disabled = this.disabled;
    }
  }

  //Bubble up the "label" property on the click event to parent element
  _handleClicked() {
    if (this.href !== undefined) {
      window.location.href = this.href;
    }
    const clickedElement = {
      label: this.value === '' ? this.label : this.value,
      icon: this.icon,
    };
    dispatchCustomEvent(this, 'clicked', clickedElement);
    this.clicked = true;
    this._toggleActive();
  }

  _toggleActive() {
    if (this.toggle) {
      this.active = !this.active;
    }
  }

  //Event to dynamically set value of sh-tool component
  _handleActiveChange() {
    //This event is used in dropdown
    dispatchCustomEvent(this, 'active-changed');
    if (this.active && !this.clicked) {
      dispatchCustomEvent(this, 'menu-item-active');
    }
    this.clicked = false;
  }

  _handleExpandIconClick() {
    if (this.expanded) {
      this._collapse();
    } else {
      this._expand();
    }
  }

  _handleExpandIconFocus(event) {
    event.target.onkeyup = (e) => {
      if (e.keyCode === KEYCODE.ARROW_LEFT) {
        this._collapse();
      } else if (e.keyCode === KEYCODE.ARROW_RIGHT) {
        this._expand();
      }
    };
  }

  _collapse() {
    this.expanded = false;
    dispatchCustomEvent(this, 'collapse', null, false);
  }

  _expand() {
    this.expanded = true;
    dispatchCustomEvent(this, 'expand', null, false);
  }

  disconnectedCallback() {
    this.removeEventListener('focus', this._handleFocus);
    super.disconnectedCallback();
  }
}
customElements.define('sh-menu-item', SHMenuItem);
